import { Platform } from "react-native";
import {
  AllProps,
  createTheme,
  ResponsiveValue,
  useTheme as UseTheme,
} from "@shopify/restyle";
import type { GradientPosition } from "types/gradient";
import {
  scale as horizontalScale,
  moderateVerticalScale,
  s,
} from "@utils/scale";

export const scaleFont = (size: number) => s(size);

export const hexToRGB = (hex: string, alpha: number) => {
  const r = parseInt(hex.slice(1, 3), 16),
    g = parseInt(hex.slice(3, 5), 16),
    b = parseInt(hex.slice(5, 7), 16);

  if (alpha) {
    return "rgba(" + r + ", " + g + ", " + b + ", " + alpha + ")";
  } else {
    return "rgb(" + r + ", " + g + ", " + b + ")";
  }
};

export const hexToRGBWorklet = (hex: string, alpha: number) => {
  "worklet";
  // Remove the hash if present
  const cleanHex = hex.charAt(0) === "#" ? hex.slice(1) : hex;

  // Convert hex to RGB
  const r = parseInt(cleanHex.slice(0, 2), 16);
  const g = parseInt(cleanHex.slice(2, 4), 16);
  const b = parseInt(cleanHex.slice(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const spacings = {
  zero: 0,
  s0_25: 1,
  s0_5: 2,
  s0_75: 3,
  s1: 4,
  s1_25: 5,
  s1_5: 6,
  s1_75: 7,
  s2: 8,
  s2_25: 9,
  s2_5: 10,
  s3: 12,
  s3_5: 14,
  s4: 16,
  m1: 20,
  m2: 24,
  l1: 32,
  l2: 40,
  l3: 52,
  xl1: 104,
};

type SpacingKey = keyof typeof spacings;
type GeneratedSpacingKey = SpacingKey | `v_${SpacingKey}` | `h_${SpacingKey}`;

const generatedSpacings = Object.entries(spacings).reduce(
  (acc, [key, value]) => {
    acc[key as SpacingKey] = value;
    acc[`v_${key}` as `v_${SpacingKey}`] = moderateVerticalScale(value, 2.5);
    acc[`h_${key}` as `h_${SpacingKey}`] = horizontalScale(value);
    return acc;
  },
  {} as Record<GeneratedSpacingKey, number>,
);

export const fonts = {
  regular: "Inter-Regular",
  medium: "Inter-Medium",
  semiBold: "Inter-SemiBold",
  bold: "Inter-Bold",
  extraBold: "Inter-ExtraBold",
} as const;

const textVariants = {
  defaults: {
    fontFamily: fonts.regular,
    fontSize: scaleFont(16),
    lineHeight: scaleFont(19),
    color: "textPrimary",
  },
  lead: {
    fontFamily: fonts.bold,
    fontSize: scaleFont(32),
    lineHeight: scaleFont(38),
    color: "textPrimary",
  },
  "lead-regular": {
    fontFamily: fonts.regular,
    fontSize: scaleFont(32),
    lineHeight: scaleFont(38),
    color: "textPrimary",
  },
  "lead-medium": {
    fontFamily: fonts.medium,
    fontSize: scaleFont(30),
    lineHeight: scaleFont(38),
    color: "textPrimary",
  },
  title: {
    fontFamily: fonts.regular,
    fontSize: scaleFont(30),
    lineHeight: scaleFont(38),
    color: "textPrimary",
  },
  "title-mid": {
    fontFamily: fonts.medium,
    fontSize: scaleFont(24),
    lineHeight: scaleFont(30),
    color: "textPrimary",
  },
  h1: {
    fontFamily: fonts.semiBold,
    fontSize: scaleFont(20),
    lineHeight: scaleFont(24),
    color: "textPrimary",
  },
  h2: {
    fontFamily: fonts.medium,
    fontSize: scaleFont(20),
    lineHeight: scaleFont(24),
    color: "textPrimary",
  },
  h3: {
    fontFamily: fonts.bold,
    fontSize: scaleFont(12),
    lineHeight: scaleFont(15),
    color: "textPrimary",
  },
  h4: {
    fontFamily: fonts.bold,
    fontSize: scaleFont(10),
    color: "textPrimary",
  },
  bodyL: {
    fontFamily: fonts.regular,
    fontSize: scaleFont(16),
    lineHeight: scaleFont(20),
    color: "textPrimary",
  },
  bodyLBold: {
    fontFamily: fonts.bold,
    fontSize: scaleFont(16),
    lineHeight: scaleFont(19),
    color: "textPrimary",
  },
  bodyM: {
    fontFamily: fonts.medium,
    fontSize: scaleFont(12),
    lineHeight: scaleFont(15),
    color: "textPrimary",
  },
  bodyM2: {
    fontFamily: fonts.semiBold,
    fontSize: scaleFont(14),
    lineHeight: scaleFont(17),
    color: "white",
  },
  bodyM2Black: {
    fontFamily: fonts.semiBold,
    fontSize: scaleFont(14),
    lineHeight: scaleFont(17),
    color: "black",
  },
  bodyM2m: {
    color: "white",
    fontFamily: fonts.medium,
    lineHeight: scaleFont(18),
    fontSize: scaleFont(14),
  },
  bodyM2ml: {
    fontFamily: fonts.medium,
    lineHeight: scaleFont(18),
    fontSize: scaleFont(15),
  },
  bodyM2Transparent: {
    fontFamily: fonts.semiBold,
    fontSize: scaleFont(14),
    lineHeight: scaleFont(17),
    color: "buttonTextPrimary",
  },
  bodyMBoldTransparent: {
    fontFamily: fonts.extraBold,
    fontSize: scaleFont(12),
    lineHeight: scaleFont(15),
    color: "buttonTextPrimary",
  },
  bodyMBold: {
    fontFamily: fonts.bold,
    fontSize: scaleFont(12),
    lineHeight: scaleFont(15),
    color: "textPrimary",
  },
  bodyMSemiBold: {
    fontFamily: fonts.semiBold,
    fontSize: scaleFont(12),
    lineHeight: scaleFont(15),
    color: "subText",
  },
  bodyS: {
    fontFamily: fonts.regular,
    fontSize: scaleFont(8),
    lineHeight: scaleFont(10),
    color: "textPrimary",
  },
  bodyS2: {
    fontFamily: fonts.medium,
    fontSize: scaleFont(10),
    lineHeight: scaleFont(10),
    color: "textPrimary",
  },
  bodyS2Black: {
    fontFamily: fonts.semiBold,
    fontSize: scaleFont(10),
    lineHeight: scaleFont(10),
    color: "black",
  },
  button: {
    fontFamily: fonts.bold,
    fontSize: scaleFont(16),
    lineHeight: scaleFont(19),
    color: "buttonTextPrimary",
  },
  buttonThin: {
    fontFamily: fonts.regular,
    fontSize: scaleFont(16),
    lineHeight: scaleFont(20),
    color: "black",
  },
  buttonMedium: {
    fontFamily: fonts.medium,
    fontSize: scaleFont(16),
    lineHeight: scaleFont(20),
    color: "buttonTextPrimary",
  },
  menuButton: {
    fontFamily: fonts.semiBold,
    fontSize: scaleFont(18),
    lineHeight: scaleFont(18),
    color: "white",
  },
  socialButton: {
    fontFamily: fonts.regular,
    fontSize: scaleFont(14),
    lineHeight: scaleFont(19),
    color: "buttonTextPrimary",
  },
  followButton: {
    fontFamily: fonts.semiBold,
    fontSize: scaleFont(12),
    color: "buttonTextPrimary",
    width: 84,
  },
  unfollowButton: {
    fontFamily: fonts.semiBold,
    fontSize: scaleFont(12),
    color: "white",
    width: 84,
  },
  listItem: {
    fontFamily: fonts.semiBold,
    fontSize: scaleFont(16),
    lineHeight: scaleFont(19),
    color: "textPrimary",
  },
  "listItem-subheader": {
    fontFamily: fonts.semiBold,
    fontSize: scaleFont(14),
    lineHeight: scaleFont(17),
    color: "subText",
  },
  inputTitle: {
    fontFamily: fonts.regular,
    fontSize: scaleFont(16),
    lineHeight: scaleFont(24),
    color: "textPrimary",
  },
  carouselTitle: {
    fontFamily: fonts.semiBold,
    fontSize: scaleFont(20),
    lineHeight: scaleFont(24),
    color: "textPrimary",
  },
  smallCardButton: {
    fontFamily: fonts.semiBold,
    fontSize: scaleFont(12),
    lineHeight: scaleFont(17),
    color: "white",
  },
  experienceTypeBox: {
    fontFamily: fonts.medium,
    fontSize: scaleFont(13),
    lineHeight: scaleFont(17),
    color: "white",
  },
  timerTitle: {
    color: "white",
    fontSize: scaleFont(14),
    fontWeight: "400",
    lineHeight: scaleFont(17),
  },
  largeHeader: {
    color: "white",
    fontSize: scaleFont(24),
    lineHeight: scaleFont(29),
    fontFamily: fonts.semiBold,
  },
  messageTitle: {
    fontSize: scaleFont(18),
    fontWeight: "600",
    lineHeight: scaleFont(23),
  },
  messageBody: {
    color: "lightGreyText",
    fontSize: scaleFont(14),
    fontWeight: "400",
    lineHeight: scaleFont(18),
    textAlign: "center",
  },
  optionTitle: {
    fontSize: scaleFont(14),
    fontWeight: "600",
    lineHeight: scaleFont(22),
  },
  collectionTitle: {
    color: "white",
    fontSize: scaleFont(20),
    fontWeight: "600",
    lineHeight: scaleFont(26),
  },
  collectionSubtitle: {
    color: "subGrey",
    fontSize: scaleFont(14),
    fontWeight: "600",
    lineHeight: scaleFont(16.8),
  },
  tryPremium: {
    color: "screenBg",
    fontSize: scaleFont(12),
    fontWeight: "600",
    lineHeight: scaleFont(22),
  },
  statsRank: {
    color: "statsSubText",
    fontSize: scaleFont(12.38),
    fontWeight: "600",
    lineHeight: scaleFont(18.56),
  },
  statsSubRank: {
    color: "statsSubText",
    fontSize: scaleFont(13),
    fontWeight: "400",
    lineHeight: scaleFont(15.6),
  },
  priceMajor: {
    color: "priceMajor",
    fontSize: scaleFont(14),
    fontWeight: "500",
  },
  priceMinor: {
    color: "priceMinor",
    fontSize: scaleFont(12),
    fontWeight: "700",
  },
  discount: {
    color: "priceMajor",
    fontSize: scaleFont(10),
    fontWeight: "700",
    lineHeight: scaleFont(10),
  },
  discountSuffix: {
    color: "priceMinor",
    fontSize: scaleFont(8),
    fontWeight: "500",
    lineHeight: scaleFont(8),
  },
  capsuleFilled: {
    fontFamily: fonts.semiBold,
    fontSize: scaleFont(12),
    lineHeight: scaleFont(15),
    color: "buttonTextPrimary",
  },
  experienceSubtitleText: {
    fontFamily: fonts.regular,
    fontSize: scaleFont(12),
    lineHeight: scaleFont(15),
    color: "experienceInstructions",
  },
  bonusTitle: {
    fontFamily: fonts.extraBold,
    fontSize: scaleFont(32),
    lineHeight: scaleFont(38),
  },
  bonusSubtitle: {
    fontFamily: fonts.semiBold,
    fontSize: scaleFont(20),
    lineHeight: scaleFont(24),
  },
  bonusCountdown: {
    fontFamily: fonts.regular,
    fontSize: scaleFont(20),
    lineHeight: scaleFont(20),
    color: "bonusGiveAwayCountdown",
  },
  artistInfoTitle: {
    fontFamily: fonts.extraBold,
    fontWeight: "800",
    fontSize: scaleFont(28),
    lineHeight: 32,
  },
  introTitle: {
    fontFamily: fonts.semiBold,
    fontSize: scaleFont(36),
    lineHeight: scaleFont(40),
    color: "white",
    textAlign: "center",
  },
  orderSummaryInfoText: {
    fontFamily: fonts.regular,
    fontSize: scaleFont(14),
    lineHeight: scaleFont(15),
    color: "lightGrey",
  },
  // new typography

  "Display/D1/Medium": {
    fontFamily: fonts.medium,
    fontSize: scaleFont(36),
    lineHeight: scaleFont(40),
    letterSpacing: -0.2,
  },
  "Display/D1/SemiBold": {
    fontFamily: fonts.semiBold,
    fontSize: scaleFont(36),
    lineHeight: scaleFont(40),
    letterSpacing: -0.2,
  },
  "Display/D1/ExtraBold": {
    fontFamily: fonts.extraBold,
    fontSize: scaleFont(36),
    lineHeight: scaleFont(40),
    letterSpacing: -0.2,
  },
  "Display/D2/Regular": {
    fontFamily: fonts.regular,
    fontSize: scaleFont(28),
    lineHeight: scaleFont(32),
    letterSpacing: -0.2,
  },
  "Display/D2/Medium": {
    fontFamily: fonts.medium,
    fontSize: scaleFont(28),
    lineHeight: scaleFont(32),
    letterSpacing: -0.2,
  },
  "Display/D2/SemiBold": {
    fontFamily: fonts.semiBold,
    fontSize: scaleFont(28),
    lineHeight: scaleFont(32),
    letterSpacing: -0.2,
  },
  "Display/D3/Regular": {
    fontFamily: fonts.regular,
    fontSize: scaleFont(24),
    lineHeight: scaleFont(28),
    letterSpacing: -0.2,
  },
  "Display/D3/Medium": {
    fontFamily: fonts.medium,
    fontSize: scaleFont(24),
    lineHeight: scaleFont(28),
    letterSpacing: -0.2,
  },
  "Display/D3/SemiBold": {
    fontFamily: fonts.semiBold,
    fontSize: scaleFont(24),
    lineHeight: scaleFont(28),
    letterSpacing: -0.2,
  },
  "Heading/H1/Regular": {
    fontFamily: fonts.regular,
    fontSize: scaleFont(20),
    lineHeight: scaleFont(24),
    letterSpacing: -0.2,
  },
  "Heading/H1/Medium": {
    fontFamily: fonts.medium,
    fontSize: scaleFont(20),
    lineHeight: scaleFont(24),
    letterSpacing: -0.2,
  },
  "Heading/H1/SemiBold": {
    fontFamily: fonts.semiBold,
    fontSize: scaleFont(20),
    lineHeight: scaleFont(24),
    letterSpacing: -0.2,
  },
  "Heading/H1/Bold": {
    fontFamily: fonts.bold,
    fontSize: scaleFont(20),
    lineHeight: scaleFont(24),
    letterSpacing: -0.2,
  },
  "Heading/H2/Regular": {
    fontFamily: fonts.regular,
    fontSize: scaleFont(18),
    lineHeight: scaleFont(22),
    letterSpacing: -0.2,
  },
  "Heading/H2/Medium": {
    fontFamily: fonts.medium,
    fontSize: scaleFont(18),
    lineHeight: scaleFont(22),
    letterSpacing: -0.2,
  },
  "Heading/H2/SemiBold": {
    fontFamily: fonts.semiBold,
    fontSize: scaleFont(18),
    lineHeight: scaleFont(22),
    letterSpacing: -0.2,
  },
  "Heading/H2/Bold": {
    fontFamily: fonts.bold,
    fontSize: scaleFont(18),
    lineHeight: scaleFont(22),
    letterSpacing: -0.2,
  },

  "Body/B1/Regular": {
    fontFamily: fonts.regular,
    fontSize: scaleFont(16),
    lineHeight: scaleFont(20),
    letterSpacing: -0.2,
  },
  "Body/B1/Medium": {
    fontFamily: fonts.medium,
    fontSize: scaleFont(16),
    lineHeight: scaleFont(20),
    letterSpacing: -0.2,
  },
  "Body/B1/SemiBold": {
    fontFamily: fonts.semiBold,
    fontSize: scaleFont(16),
    lineHeight: scaleFont(20),
    letterSpacing: -0.2,
  },
  "Body/B1/Bold": {
    fontFamily: fonts.bold,
    fontSize: scaleFont(16),
    lineHeight: scaleFont(20),
    letterSpacing: -0.2,
  },
  "Body/B2/Regular": {
    fontFamily: fonts.regular,
    fontSize: scaleFont(14),
    lineHeight: scaleFont(18),
    letterSpacing: -0.2,
  },
  "Body/B2/Medium": {
    fontFamily: fonts.medium,
    fontSize: scaleFont(14),
    lineHeight: scaleFont(18),
    letterSpacing: -0.2,
  },
  "Body/B2/SemiBold": {
    fontFamily: fonts.semiBold,
    fontSize: scaleFont(14),
    lineHeight: scaleFont(18),
    letterSpacing: -0.2,
  },
  "Body/B2/Bold": {
    fontFamily: fonts.bold,
    fontSize: scaleFont(14),
    lineHeight: scaleFont(18),
    letterSpacing: -0.2,
  },

  "Caption/C1/Regular": {
    fontFamily: fonts.regular,
    fontSize: scaleFont(12),
    lineHeight: scaleFont(16),
    letterSpacing: -0.2,
  },
  "Caption/C1/Medium": {
    fontFamily: fonts.medium,
    fontSize: scaleFont(12),
    lineHeight: scaleFont(16),
    letterSpacing: -0.2,
  },
  "Caption/C1/SemiBold": {
    fontFamily: fonts.semiBold,
    fontSize: scaleFont(12),
    lineHeight: scaleFont(16),
    letterSpacing: -0.2,
  },
  "Caption/C1/Bold": {
    fontFamily: fonts.bold,
    fontSize: scaleFont(12),
    lineHeight: scaleFont(16),
    letterSpacing: -0.2,
  },
  "Caption/C2/Regular": {
    fontFamily: fonts.regular,
    fontSize: scaleFont(10),
    lineHeight: scaleFont(14),
    letterSpacing: -0.2,
  },
  "Caption/C2/Medium": {
    fontFamily: fonts.medium,
    fontSize: scaleFont(10),
    lineHeight: scaleFont(14),
    letterSpacing: -0.2,
  },
  "Caption/C2/SemiBold": {
    fontFamily: fonts.semiBold,
    fontSize: scaleFont(10),
    lineHeight: scaleFont(14),
    letterSpacing: -0.2,
  },
  "Caption/C2/Bold": {
    fontFamily: fonts.bold,
    fontSize: scaleFont(10),
    lineHeight: scaleFont(14),
    letterSpacing: -0.2,
  },

  "Mini/M1/Regular": {
    fontFamily: fonts.regular,
    fontSize: scaleFont(8),
    lineHeight: scaleFont(12),
    letterSpacing: -0.2,
  },
  "Mini/M1/Medium": {
    fontFamily: fonts.medium,
    fontSize: scaleFont(8),
    lineHeight: scaleFont(12),
    letterSpacing: -0.2,
  },
  "Mini/M1/SemiBold": {
    fontFamily: fonts.semiBold,
    fontSize: scaleFont(8),
    lineHeight: scaleFont(12),
    letterSpacing: -0.2,
  },
  "Mini/M1/Bold": {
    fontFamily: fonts.bold,
    fontSize: scaleFont(8),
    lineHeight: scaleFont(12),
    letterSpacing: -0.2,
  },
};

const palette = {
  algae: "#496A72",
  black: "#0B0B0B",
  blueGreen: "#08435A",
  candy: "#FF75E8",
  cherry: "#DA5263",
  condor: "#2F2E3E",
  cyber: "#033A46",
  cynicalBlack: "#171717",
  darkBrown: "#4c3a39",
  darkPurple: "#732ce7",
  deepSea: "#15324D",
  grey: "#79797E",
  lightPurple: "#a88beb",
  mustard: "#F6B452",
  nero: "#212121",
  nightOcean: "#1C142E",
  nightOceanTranslucent: "#1C142E70",
  ocean: "#083B4F",
  onyx: "#373737",
  red: "red",
  slate: "#222233",
  softNight: "#171A1C",
  transparent: "transparent",
  tropical: "#F29951",
  turquoise: "#132C45",
  white: "#FFFFFF",
  whitePink: "#FFEEF8",

  // modal
  artistBlue: "#1D71B8",
  artistGreen: "#50CD89",
  saveBlue: "#3880E4",
  backdrop: "rgba(0, 0, 0, 0.4)",
  barMusicPlayerBg: hexToRGB("#0B0C0D", 0.75),
  blueTitle: "#E5EDFF",
  boostButtonLeft: "#FF5F6D",
  boostButtonRight: "#FFC371",
  buttonGrey: "#7A7A8D",
  challengeBadge: "#11272F",
  cool: "#13556F",
  darkBlue: "#082831",
  darkGrey: "#959595",
  experienceBg: "#0A3945",
  experienceBorder: "#104B5B",
  experienceDarkPurple: "#722AE6",
  experienceGreen1: "#009245",
  experienceGreen2: "#84DE8D",
  experienceGreenMidCircle: "#4FBF71",
  gray: "#444350",
  updateGray: "#404040",
  greyBorder: "#434350",
  leaderboardBlack: "#15151E",
  leaderboardFirst: "#B17304",
  leaderboardSecond: "#868E8F",
  leaderboardThird: "#D29148",
  lightBlue: "#D9D9D9",
  lightGray: "#90909D",
  lightGrey: "#C4C4C4",
  lightGrey2: "#CFCFCF",
  lightGreyText: "#A2A2AE",
  marketplaceGreen: "#079545",
  marketplaceYellow: "#F8D94B",
  oceanBlue: "#0D2D3E",
  placeholderGrey: "rgba(255,255,255,0.4)",
  playerQueueBg1: "rgba(218, 82, 99, 0.95)",
  playerQueueBg2: "rgba(242, 153, 81, 0.95)",
  primaryGrey: "#7A7A8D",
  profile: "#0A3945",
  screenBg: "#0B0C0D",
  secondaryGrey: "#3f3d52",
  slate2: "#292839",
  timerRed: "#F01040",
  transparentBlack: "#00000030",
  transparentGrey: "rgba(127, 128, 134, 0.5)",
  translucentWhite: "rgba(255, 255, 255, 0.1)",
  halfBlack: "rgba(0, 0, 0, 0.5)",
  blackTranslucent: "rgba(0, 0, 0, 0.8)",
  blackTranslucent10: "rgba(0, 0, 0, 0.1)",
  blackTranslucent20: "rgba(0, 0, 0, 0.2)",
  blackTranslucent30: "rgba(0, 0, 0, 0.3)",
  blackTranslucent40: "rgba(0, 0, 0, 0.4)",
  blackTranslucent50: "rgba(0, 0, 0, 0.5)",
  blackTranslucent60: "rgba(0, 0, 0, 0.6)",
  blackTranslucent70: "rgba(0, 0, 0, 0.7)",
  blackTranslucent80: "rgba(0, 0, 0, 0.8)",
  blackTranslucent90: "rgba(0, 0, 0, 0.9)",
  veryBlue: "#4697f7",
  veryLightBlue: "#ACC4CA",
  greyText: "#909090",
  lightBlack: "#333333",
  vDarkGrey: "#1e1e1e",
  albumOrange: "#E67E22B2",
  playlistBlue: "#13556F",
  subTextGrey: "#9d9e9e",
  capsuleDefaultBg: "#2B2E31",
  lightEmerald: "#9BE15D",
  darkTurquoise: "#00C9A7",
  lightEmeraldTranslucent: "#9BE15D70",
  darkTurquoiseTranslucent: "#00C9A770",
  premiumOrangeTranslucent: "rgba(242, 153, 81, 0.08)",
  premiumOrangeTranslucent2: "rgba(242, 153, 81,0.5)",
  ghostGrey: "#434647",
  mediumGrey: "#9399A3",
  gemOrange: "#F29951",
  gemOrangeTranslucent: "rgba(242, 153, 81, 0.1)",
  halfTranslucentWhite: "rgba(255, 255, 255, 0.5)",
  tileGrey: "#2E3133",
  greyButton: "#232527",
  gemGray: "rgba(217, 217, 217, 0.5)",
  bonusGiveAwayTitle: "#FBBB50",
  bonusGiveAwayGradientLeft: "#FFA79F",
  bonusGiveAwayGradientRight: "#FCBC50",
  bonusGiveAwayCountdown: "#CCCCCC",
  disabledText: "#ffffff60",
  addToRosterBtn1: "#F6B12F",
  addToRosterBtn2: "#FF5722",
  gold2: "#F6B12F",
  gold1: "#FFFCA8",
  unlockButtonText: "#AE2C00",
  accessBadgeBg: "#ABAEB580",
  accessTypeConcertBg: "#221206",
  accessTypeConcertText: "#FFBE84",
  accessTypeMusicBg: "#04241F",
  accessTypeMusicText: "#66E8C3",
  accessTypeOnlineBg: "#082F3B",
  accessTypeOnlineText: "#8DDEED",
  accessTypeMerchBg: "#1E1033",
  accessTypeMerchText: "#C9A8FF",

  darkGrey2: "#2F3133",
  accessBadgeBgGray: "#DADADA",
  accessItemBorder: "#535353",
  accessPreviewProgressBar: "#FF5722",
  accessModalSubtitle: "#A2A3A4",
  accessModalPriceBg: "#121212",
  introBgGradient1: "#1C142E",
  introBgGradient2: "#0C2C3D",
  introBgGradient3: "#043845",
  onboardingSkipButtonBg: "#171819",
  paywallPointIconBg: "#212936",
  searchHistorySeeAllText: "#7E7E7F",
  navigatorInactiveLabel: "#9E9E9E",
  playlistImportCompleteTitle: "#A1ECB1",
  playlistImportCompleteSubtitle: "#A1ECB0",
  playlistImportCompleteBg: "#11271F",
  playlistImportCompleteBorder: "#285634",
  orderSummaryBg: "#272B2D",
  successGreen: "#A1EDB0",

  // Orbit gradients
  orbitGold1: "#AE8625",
  orbitGold2: "#F7EF8A",
  orbitGold3: "#D2AC47",
  orbitGold4: "#EDC967",
  orbitSilver1: "#C2E8FD",
  orbitSilver2: "#919191",
  orbitSilver3: "#C2E8FD",
  orbitSilver4: "#FFFFFF",
  orbitSilver5: "#DDDDDD",
};

export const theme = createTheme({
  colors: {
    // whiteTranslucent: palette.whiteTranslucent,
    //collection screen colors
    activeIndicatorLeft: palette.cherry,
    activeIndicatorRight: palette.tropical,
    activityIndicator: palette.candy,
    additiveText: hexToRGB(palette.screenBg, 0.5),
    albumBottomBg: palette.black,
    albumTopBg: palette.albumOrange,
    artistBlue: palette.artistBlue,
    artistGreen: palette.artistGreen,
    saveBlue: palette.saveBlue,
    artistSubtitleText: palette.greyText,
    artistTitleText: palette.mustard,
    authButton: palette.buttonGrey,
    authButtonActive: palette.nightOcean,
    authButtonBg: palette.experienceBg,
    authInputBackground: palette.gray,
    backgroundBottom: palette.nightOcean,
    backgroundBottomTranslucent: hexToRGB(palette.nightOcean, 0.85),
    backgroundTop: palette.cyber,
    backgroundTopTranslucent: hexToRGB(palette.cyber, 0.85),
    barChartBarBottom: palette.mustard,
    barChartBarBottom2: palette.cherry,
    barChartBarBottom3: palette.artistGreen,
    barChartBarTop: palette.mustard,
    barChartBarTop2: palette.cherry,
    barChartBarTop3: palette.artistGreen,
    barMusicPlayerBg: palette.barMusicPlayerBg,
    barPlayerBg: palette.darkBrown,
    barPlayerProgressBarBackground: palette.grey,
    barPlayerProgressBarProgressLeft: palette.cherry,
    barPlayerProgressBarProgressRight: palette.tropical,
    barPlayerSubtitle: palette.white,
    barPlayerTitle: palette.whitePink,
    black: palette.black,
    blackTranslucent: palette.blackTranslucent,
    blackTranslucent10: palette.blackTranslucent10,
    boostButtonLeft: palette.boostButtonLeft,
    boostButtonRight: palette.boostButtonRight,
    bonusGiveAwayTitle: palette.bonusGiveAwayTitle,
    bonusDirectTitle: palette.lightEmerald,
    bonusGiveAwayGradientLeft: palette.bonusGiveAwayGradientLeft,
    bonusGiveAwayGradientRight: palette.bonusGiveAwayGradientRight,
    bonusDirectGradientLeft: palette.lightEmerald,
    bonusDirectGradientRight: palette.darkTurquoise,
    bonusGiveAwayCountdown: palette.bonusGiveAwayCountdown,
    browseItemBadgeBackground: palette.nightOceanTranslucent,
    buttonBackgroundPrimary: palette.white,
    buttonBackgroundPrimaryLeft: palette.cherry,
    buttonBackgroundPrimaryRight: palette.tropical,
    buttonDarkBackground: palette.onyx,
    buttonTextPrimary: palette.nightOcean,
    capsuleDefaultBg: palette.capsuleDefaultBg,
    challengeActionColor: palette.veryBlue,
    challengeBadge: palette.challengeBadge,
    challengeCardBg: palette.slate,
    challengeGradientLeft: palette.lightPurple,
    challengeGradientRight: palette.darkPurple,
    challengeGradientText: palette.ocean,
    challengesBg: palette.blueGreen,
    challengeText: palette.condor,
    chartLabel: palette.whitePink,
    chartLine: palette.veryLightBlue,
    chartLineMain: palette.tropical,
    checkInactiveBg: palette.grey,
    closeButtonBg: palette.nero,
    collectionModalBackgroundBottomTranslucent: palette.nightOcean,
    collectionModalBackgroundTopTranslucent: palette.cherry,
    comingSoonBadgeBg: palette.mustard,
    comingSoonBadgeText: palette.black,
    concertBadgeBg: palette.softNight,
    accessTypeConcertBg: palette.accessTypeConcertBg,
    accessTypeConcertText: palette.accessTypeConcertText,
    accessTypeMusicBg: palette.accessTypeMusicBg,
    accessTypeMusicText: palette.accessTypeMusicText,
    accessTypeOnlineBg: palette.accessTypeOnlineBg,
    accessTypeOnlineText: palette.accessTypeOnlineText,
    accessTypeMerchBg: palette.accessTypeMerchBg,
    accessTypeMerchText: palette.accessTypeMerchText,
    creditsTitleText: palette.mustard,
    danger: palette.cherry,
    darkGrey: palette.darkGrey,
    dateTimePickerBg: palette.condor,
    debug: palette.red,
    default: palette.white,
    entriesBalanceBorder: hexToRGB(palette.tropical, 0.25),
    entriesBalanceBottom: hexToRGB(palette.tropical, 0.1),
    entriesBalanceTop: hexToRGB(palette.tropical, 0.4),
    error: palette.red,
    experienceBackBg: palette.softNight,
    experienceBackBorder: palette.transparentGrey,
    experienceBadge: palette.mustard,
    experienceBg: palette.experienceBg,
    experienceBorder: palette.experienceBorder,
    experienceCardBg: palette.softNight,
    experienceDarkPurple: palette.experienceDarkPurple,
    experienceGreenBottom: palette.experienceGreen2,
    experienceGreenMiddle: palette.experienceGreenMidCircle,
    experienceGreenTop: palette.experienceGreen1,
    experienceInstructions: palette.greyText,
    experienceLissenerAccountTileBg: palette.nightOcean,
    experienceLissenerAccountTileContent: palette.secondaryGrey,
    experienceSubBg: palette.onyx,
    faintWhite: palette.translucentWhite,
    favouriteTileBg: palette.softNight,
    favouriteTileGradientLeft: palette.cherry,
    favouriteTileGradientRight: palette.tropical,
    featureCarouselBackgroundBottom: hexToRGB(palette.nightOcean, 1.0),
    featureCarouselBackgroundTop: hexToRGB(palette.nightOcean, 0.01),
    featureCarouselBarActiveLeft: palette.tropical,
    featureCarouselBarActiveRight: palette.cherry,
    featureCarouselBarInactive: palette.grey,
    gemsProgressTrackGradientLeft: palette.lightEmerald,
    gemsProgressTrackGradientRight: palette.darkTurquoise,
    gemLabelBackgroundGradientBottom: hexToRGB(palette.gemOrange, 0.2),
    gemLabelBackgroundGradientTop: palette.gemOrange,
    genreText: palette.slate,
    ghostGrey: palette.ghostGrey,
    giftGradientLeft: palette.artistGreen,
    giftGradientRight: palette.artistBlue,
    greyBorder: palette.greyBorder,
    greyText: palette.greyText,
    heatmapBackground: palette.condor,
    heatMapCircle: palette.algae,
    heatMapCirclePremiumBottom: palette.tropical,
    heatMapCirclePremiumTop: palette.cherry,
    heatmapMapLevelBackground: palette.condor,
    heatmapMapLevelSelectedLeft: palette.candy,
    heatmapMapLevelSelectedRight: palette.tropical,
    heatmapMapLevelUnselected: palette.white,
    homeArtistCardBackground: palette.condor,
    homeGreeting: palette.mustard,
    homeLibraryItemBackgroundBright: palette.algae,
    homeLibraryItemBackgroundDark: palette.deepSea,
    homeLibraryItemBg: palette.blueGreen,
    homeLibraryItemShadowLeft: palette.cherry,
    homeLibraryItemShadowRight: palette.tropical,
    homeLibraryItemText: palette.white,
    homeScreenItemStack1: palette.buttonGrey,
    homeScreenItemStack2: palette.lightGray,
    homeSectionHeaderTitle: palette.whitePink,
    inactiveBg: palette.grey,
    inactiveText: palette.grey,
    inboxDate: palette.greyText,
    inputBackgroundPrimary: palette.onyx,
    inputBackgroundSecondary: palette.whitePink,
    inputBackgroundTertiary: palette.nero,
    inputPlaceholder: palette.placeholderGrey,
    inputTextPrimary: palette.whitePink,
    inputTextSecondary: palette.nightOcean,
    instructionsText: palette.greyText,
    largeTileGradientLeft: palette.cherry,
    largeTileGradientRight: palette.tropical,
    lead: palette.tropical,
    leaderboardBlack: palette.leaderboardBlack,
    leaderboardFirst: palette.leaderboardFirst,
    leaderboardRank: palette.condor,
    leaderboardSecond: palette.leaderboardSecond,
    leaderboardThird: palette.leaderboardThird,
    leaderboardTitle: palette.blueTitle,
    levelProgressTrack: palette.white,
    libraryHeaderBorder: palette.white,
    libraryHeaderMenuActive: palette.mustard,
    libraryHeaderMenuInactive: palette.white,
    libraryItemBackground: palette.white,
    libraryItemBorder: palette.white,
    libraryItemIcon: palette.white,
    lightBlack: palette.lightBlack,
    lightGrey: palette.lightGrey,
    lightGrey2: palette.lightGrey2,
    lightGreyBorder: palette.veryLightBlue,
    lightGreyText: palette.lightGreyText,
    linkBg: palette.slate,
    linkButtonText: palette.tropical,
    linkText: palette.mustard,
    lissenLogoBg1: palette.cyber,
    lissenLogoBg2: palette.slate,
    lissenLogoBorder1: palette.cherry,
    lissenLogoBorder2: palette.tropical,
    listItemBackground: palette.condor,
    listItemHeaderText: palette.mustard,
    listItemSubHeaderText: palette.white,
    listItemText: palette.whitePink,
    marketplaceGradientLeft: palette.marketplaceGreen,
    marketplaceGradientRight: palette.marketplaceYellow,
    mediumGrey: palette.mediumGrey,
    miniPlayerBackground: palette.cynicalBlack,
    modalBackdrop: palette.backdrop,
    modalBorder: palette.cherry,
    modalContentBackground: palette.nero,
    musicPlayerBottom: palette.slate,
    musicPlayerTop: palette.cyber,
    navigatorInactiveLabel: palette.navigatorInactiveLabel,
    numberSelectorBg: palette.tileGrey,
    playerQueueBg1: palette.playerQueueBg1,
    playerQueueBg2: palette.playerQueueBg2,
    playlistBottomBg: palette.black,
    playlistTopBg: palette.playlistBlue,
    premiumContentBg: palette.slate2,
    premiumContentText: palette.mustard,
    premiumModalBg: palette.slate,
    premiumTileGradientBottom: palette.transparentBlack,
    premiumTileGradientTop: palette.premiumOrangeTranslucent,
    priceMajor: palette.white,
    priceMinor: palette.halfTranslucentWhite,
    profile: palette.nero,
    profileCardBg: palette.profile,
    progressTrackBg: palette.primaryGrey,
    queueHeader: palette.lightGrey,
    rewardText: palette.grey,
    screenBg: palette.screenBg,
    searchBoxClearButton: palette.screenBg,
    searchInputBackground: palette.white,
    searchInputPlaceholderText: palette.grey,
    searchInputText: palette.condor,
    searchResultSeeAllText: palette.searchHistorySeeAllText,
    selectBackground: palette.nero,
    selectedSoundIcon: palette.whitePink,
    selectedSoundText: palette.whitePink,
    separator: palette.veryLightBlue,
    shareBg: palette.darkBlue,
    sliderLeft: palette.cherry,
    sliderRight: palette.grey,
    sliderThumb: palette.white,
    softNight: palette.softNight,
    soundArtistText: palette.grey,
    soundPlayingText: palette.mustard,
    soundLockedText: palette.disabledText,
    statsHeaderText: palette.slate,
    statsPanelBackground: palette.slate,
    statsSubHeaderText: palette.grey,
    statsProgressTrackBg: palette.greyText,
    statsSubText: hexToRGB(palette.white, 0.5),
    subGrey: hexToRGB(palette.white, 0.6),
    subText: palette.primaryGrey,
    subTextGrey: palette.subTextGrey,
    successAlertText: palette.slate,
    tabActiveBg: hexToRGB(palette.white, 0.07),
    tabActiveHomeBg: hexToRGB(palette.white, 0.15),
    tabActiveBorder: hexToRGB(palette.white, 0.13),
    tabBar: palette.slate,
    tabBarBg: hexToRGB("#171A1C", 0.7),
    tabBarBorder: palette.slate,
    tabLabelLeft: palette.cherry,
    tabLabelRight: palette.tropical,
    textGrey: palette.primaryGrey,
    textPrimary: palette.whitePink,
    tileGradientBottom: hexToRGB("#000000", 0),
    tileGradientTop: hexToRGB(palette.white, 0.08),
    toastBg: palette.grey,
    toastMessageText: palette.tropical,
    transparent: palette.transparent,
    transparentBlack: palette.transparentBlack,
    turquoise: palette.turquoise,
    unreadNotification: palette.cherry,
    updateGray: palette.updateGray,
    videocallBg: palette.transparentGrey,
    videocallTimerBg: palette.transparentGrey,
    videocallTimerWarningBg: palette.timerRed,
    voucherPublicBorderGradientLeft: palette.cherry,
    voucherPublicBorderGradientRight: palette.gemOrange,
    voucherPrivateBorderGradientBottom: palette.darkTurquoise,
    voucherPrivateBorderGradientTop: palette.lightEmerald,
    voucherTileBg: palette.tileGrey,
    white: palette.white,
    whiteBorder: hexToRGB(palette.white, 0.1),
    whiteTranslucent: hexToRGB(palette.white, 0.3),
    whiteTranslucent10: hexToRGB(palette.white, 0.1),
    whiteTranslucent20: hexToRGB(palette.white, 0.2),
    whiteTranslucent50: hexToRGB(palette.white, 0.5),
    whiteTranslucent60: hexToRGB(palette.white, 0.6),
    whiteTranslucent70: hexToRGB(palette.white, 0.7),
    whiteTranslucent80: hexToRGB(palette.white, 0.8),

    greyTranslucent10: hexToRGB(palette.darkGrey2, 0.1),
    greyTranslucent20: hexToRGB(palette.darkGrey2, 0.2),
    greyTranslucent30: hexToRGB(palette.darkGrey2, 0.3),
    greyTranslucent40: hexToRGB(palette.darkGrey2, 0.4),
    greyTranslucent50: hexToRGB(palette.darkGrey2, 0.5),
    greyTranslucent60: hexToRGB(palette.darkGrey2, 0.6),
    greyTranslucent70: hexToRGB(palette.darkGrey2, 0.7),
    greyTranslucent80: hexToRGB(palette.darkGrey2, 0.8),
    greyTranslucent90: hexToRGB(palette.darkGrey2, 0.9),

    greyButton: palette.greyButton,
    gemOrange: palette.gemOrange,
    gemGray: palette.gemGray,
    halfBlack: palette.halfBlack,
    gemOrangeTranslucent: palette.gemOrangeTranslucent,
    privateContentTop: palette.lightEmerald,
    privateContentBottom: palette.darkTurquoise,
    privateContentUnlockedTop: palette.lightEmeraldTranslucent,
    privateContentUnlockedBottom: palette.darkTurquoiseTranslucent,
    exclusiveGold: palette.mustard,
    addToRosterBtn1: palette.addToRosterBtn1,
    addToRosterBtn2: palette.addToRosterBtn2,
    accessBadgeBg: hexToRGB(palette.accessBadgeBg, 0.5),

    halfTranslucentWhite: palette.halfTranslucentWhite,
    black70: hexToRGB("#000000", 0.7),
    priceTagBg: hexToRGB("#0B0C0DCC", 0.8),
    feedNotLoggedInBg: palette.darkGrey2,
    feedNotLoggedInSubText: palette.greyText,
    unlockButtonText: palette.unlockButtonText,
    dateTagBg: "#F1F1F1",
    accessBadgeBgGray: palette.accessBadgeBgGray,
    accessItemBorder: palette.accessItemBorder,
    accessItemDateBg: palette.darkGrey2,
    accessPreviewProgressBar: palette.accessPreviewProgressBar,
    accessModalSubtitle: palette.accessModalSubtitle,
    accessModalPriceBg: palette.accessModalPriceBg,
    introBgGradient1: palette.introBgGradient1,
    introBgGradient2: palette.introBgGradient2,
    introBgGradient3: palette.introBgGradient3,
    onboardingSkipButtonBg: palette.onboardingSkipButtonBg,
    paywallPointIconBg: palette.paywallPointIconBg,
    subtitleGrey: palette.buttonGrey,
    playlistImportCompleteTitle: palette.playlistImportCompleteTitle,
    playlistImportCompleteSubtitle: hexToRGB(
      palette.playlistImportCompleteSubtitle,
      0.6,
    ),
    playlistImportCompleteBg: palette.playlistImportCompleteBg,
    playlistImportCompleteBorder: palette.playlistImportCompleteBorder,
    orderSummaryBg: palette.orderSummaryBg,
    accessCheckoutValidationSuccess: palette.successGreen,

    // Orbit gradients
    orbitGold1: palette.orbitGold1,
    orbitGold2: palette.orbitGold2,
    orbitGold3: palette.orbitGold3,
    orbitGold4: palette.orbitGold4,
    orbitSilver1: palette.orbitSilver1,
    orbitSilver2: palette.orbitSilver2,
    orbitSilver3: palette.orbitSilver3,
    orbitSilver4: palette.orbitSilver4,
    orbitSilver5: palette.orbitSilver5,

    blackTranslucent20: palette.blackTranslucent20,
    blackTranslucent30: palette.blackTranslucent30,
    blackTranslucent40: palette.blackTranslucent40,
    blackTranslucent50: palette.blackTranslucent50,
    blackTranslucent60: palette.blackTranslucent60,
    blackTranslucent70: palette.blackTranslucent70,
    blackTranslucent80: palette.blackTranslucent80,
    blackTranslucent90: palette.blackTranslucent90,
    gold1: palette.gold1,
    gold2: palette.gold2,
  },
  spacing: generatedSpacings,
  borderRadii: {
    xs: 2,
    s: 4,
    "s-m": 5,
    "s-s": 6,
    m: 8,
    "m-m": 10,
    "m-l": 12,
    l: 16,
    "l-xl": 20,
    xl: 24,
    xxl: 32,
    xxll: 36,
    xxxl: 40,
    xxxll: 44,
    max: 9999,
    zero: 0,
  },
  breakpoints: {
    phone: 0,
    tablet: 768,
  },
  textVariants,
  inputVariants: {
    defaults: {
      fontFamily: fonts.regular,
      fontSize: scaleFont(16),
      backgroundColor: "favouriteTileBg",
      paddingHorizontal: "s2",
      paddingVertical: "s4",
      color: "inputTextPrimary",
      borderRadius: "s",
    },
    auth: {
      fontFamily: fonts.semiBold,
      fontSize: scaleFont(16),
      fontWeight: "600",
      backgroundColor: "favouriteTileBg",
      paddingHorizontal: "s3",
      paddingVertical: "s4",
      color: "textPrimary",
      borderRadius: "m",
    },
    primary: {
      backgroundColor: "inputBackgroundPrimary",
      borderColor: "whiteBorder",
      borderRadius: "m",
      borderWidth: 1,
      color: "white",
      fontFamily: fonts.semiBold,
      fontSize: scaleFont(16),
      fontWeight: "600",
      paddingHorizontal: "s3",
      paddingVertical: "s3_5",
    },
    profile: {
      fontFamily: fonts.semiBold,
      fontSize: scaleFont(16),
      backgroundColor: "favouriteTileBg",
      paddingHorizontal: "s2",
      paddingVertical: "s4",
      color: "default",
      borderRadius: "s",
    },
    searchBar: {
      fontFamily: fonts.regular,
      fontSize: scaleFont(Platform.OS === "web" ? 16 : 14),
      backgroundColor: "searchInputBackground",
      color: "searchInputText",
      paddingHorizontal: Platform.OS === "web" ? "s2" : "s3_5",
      paddingVertical: Platform.OS === "web" ? "s2" : "s3",
      borderRadius: "xxxl",
    },
    searchBarSmall: {
      fontFamily: fonts.regular,
      fontSize: scaleFont(16),
      backgroundColor: "searchInputBackground",
      color: "searchInputText",
      paddingHorizontal: "s2",
      paddingVertical: "s2",
      borderRadius: "s",
    },
    searchBarSecondary: {
      fontFamily: fonts.regular,
      fontSize: scaleFont(14),
      backgroundColor: "inputBackgroundTertiary",
      paddingHorizontal: "s2",
      paddingVertical: "s4",
      color: "inputTextPrimary",
      borderRadius: "s",
    },
    priceInput: {
      fontFamily: fonts.regular,
      fontSize: scaleFont(16),
      backgroundColor: "transparent",
      color: "white",
      paddingHorizontal: "s2",
      paddingVertical: "s2",
      borderRadius: "s",
    },
    accessCheckout: {
      fontFamily: fonts.regular,
      fontSize: textVariants["Body/B1/Regular"].fontSize,
      lineHeight: textVariants["Body/B1/Regular"].lineHeight,
      letterSpacing: textVariants["Body/B1/Regular"].letterSpacing,
      color: "white",
      backgroundColor: "transparent",
      paddingVertical: "zero",
    },
  },
});

export function useTheme() {
  return UseTheme<Theme>();
}

//@ts-expect-error Property colors is incompatible with index signature
export type BackgroundColor = ResponsiveValue<keyof Theme["colors"], Theme>;

export type LBoxProps = Omit<AllProps<Theme>, "backgroundColor"> & {
  backgroundColor?: BackgroundColor | BackgroundColor[];
};

export type TextColor = ResponsiveValue<
  keyof Theme["colors"],
  Theme["breakpoints"]
>;

type LGradientProps = {
  blurAmount?: number;
  gradientDirection?: "horizontal" | "vertical";
  gradientPosition?: GradientPosition;
  locations?: number[];
};

type VariantProps = LBoxProps & LGradientProps;

export const buttonVariants: {
  "tertiary-destructive": VariantProps;
  "tertiary-small": VariantProps;
  auth: VariantProps;
  authActive: VariantProps;
  capsule: VariantProps;
  capsuleFilled: VariantProps;
  defaults: VariantProps;
  disabled: VariantProps;
  experiencePrimary: VariantProps;
  followArtist: VariantProps;
  orange: VariantProps;
  orangeThick: VariantProps;
  primary: VariantProps;
  round: VariantProps;
  roundedGhost: VariantProps;
  roundedWhite: VariantProps;
  roundedWhiteEmpty: VariantProps;
  roundedGreyEmpty: VariantProps;
  roundSmall: VariantProps;
  slate: VariantProps;
  tertiary: VariantProps;
  tryPremium: VariantProps;
  turquoise: VariantProps;
  joinArtist: VariantProps;
  updateButton: VariantProps;
  updateButtonSecondary: VariantProps;
  bonusGiveAwayButton: VariantProps;
  bonusDirectButton: VariantProps;
  unlock: VariantProps;
} = {
  "tertiary-destructive": {
    backgroundColor: "transparent",
    borderColor: "danger",
    borderWidth: 1,
    color: "danger",
    borderRadius: "max",
  },
  "tertiary-small": {
    backgroundColor: "transparent",
    borderColor: "white",
    borderWidth: 1,
    color: "white",
    paddingHorizontal: "s2",
    paddingVertical: "s1",
  },
  auth: {
    fontFamily: fonts.semiBold,
    backgroundColor: "authButton",
    color: "default",
    paddingVertical: "s4",
    borderRadius: "max",
  },
  authActive: {
    fontFamily: fonts.semiBold,
    backgroundColor: [
      "buttonBackgroundPrimaryLeft",
      "buttonBackgroundPrimaryRight",
    ],
    gradientDirection: "horizontal",
    paddingVertical: "s4",
    color: "authButtonActive",
    borderRadius: "max",
  },
  capsule: {
    paddingHorizontal: "s4",
    paddingVertical: "s2",
    borderRadius: "max",
    color: "white",
    backgroundColor: "capsuleDefaultBg",
  },
  roundedWhite: {
    paddingHorizontal: "s4",
    paddingVertical: "s4",
    borderRadius: "max",
    color: "black",
    backgroundColor: "white",
  },
  capsuleFilled: {
    paddingHorizontal: "s4",
    paddingVertical: "s2",
    borderRadius: "max",
    color: "buttonTextPrimary",
    backgroundColor: "transparent",
  },
  followArtist: {
    paddingHorizontal: "s4",
    paddingVertical: "s1_5",
    borderRadius: "max",
    color: "black",
    backgroundColor: "white",
    borderWidth: 1,
    borderColor: "transparent",
  },
  joinArtist: {
    paddingHorizontal: "s4",
    paddingVertical: "s1_5",
    borderRadius: "max",
    color: "white",
    fontSize: 14,
    backgroundColor: "screenBg",
    borderWidth: 1,
    borderColor: "whiteTranslucent50",
  },
  defaults: {
    paddingHorizontal: "s4",
    paddingVertical: "s3",
    borderRadius: "s",
    width: "auto",
  },
  disabled: {
    backgroundColor: "authButton",
    color: "default",
  },
  experiencePrimary: {
    backgroundColor: [
      "buttonBackgroundPrimaryLeft",
      "buttonBackgroundPrimaryRight",
    ],
    paddingHorizontal: "s4",
    paddingVertical: "s4",
    borderRadius: "m",
    fontSize: 20,
    gradientDirection: "horizontal",
    color: "screenBg",
  },
  orange: {
    paddingVertical: "s2",
    backgroundColor: [
      "buttonBackgroundPrimaryLeft",
      "buttonBackgroundPrimaryRight",
    ],
  },
  orangeThick: {
    paddingHorizontal: "s4",
    paddingVertical: "s4",
    borderRadius: "max",
    width: "auto",
    backgroundColor: [
      "buttonBackgroundPrimaryLeft",
      "buttonBackgroundPrimaryRight",
    ],
  },
  primary: {
    backgroundColor: [
      "buttonBackgroundPrimaryLeft",
      "buttonBackgroundPrimaryRight",
    ],
    gradientDirection: "horizontal",
  },
  round: {
    backgroundColor: [
      "buttonBackgroundPrimaryLeft",
      "buttonBackgroundPrimaryRight",
    ],
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "max",
    aspectRatio: 1,
    width: 50,
  },
  roundSmall: {
    backgroundColor: [
      "buttonBackgroundPrimaryLeft",
      "buttonBackgroundPrimaryRight",
    ],
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "max",
    aspectRatio: 1,
    width: 25,
    height: 25,
    paddingHorizontal: "zero",
    paddingVertical: "zero",
  },
  slate: {
    paddingVertical: "s3",
    borderRadius: "s",
    backgroundColor: "challengeCardBg",
    color: "artistGreen",
  },
  tertiary: {
    backgroundColor: "transparent",
    borderColor: "white",
    borderWidth: 1,
    color: "white",
  },
  turquoise: {
    paddingHorizontal: "s4",
    paddingVertical: "s3",
    borderRadius: "s",
    backgroundColor: "turquoise",
  },
  updateButton: {
    borderRadius: "m-l",
    backgroundColor: [
      "buttonBackgroundPrimaryLeft",
      "buttonBackgroundPrimaryRight",
    ],
    textTransform: "uppercase",
    paddingHorizontal: "l2",
  },
  updateButtonSecondary: {
    borderRadius: "m-l",
    backgroundColor: "updateGray",
    textTransform: "uppercase",
    color: "linkButtonText",
  },
  tryPremium: {
    backgroundColor: [
      "buttonBackgroundPrimaryLeft",
      "buttonBackgroundPrimaryRight",
    ],
    gradientDirection: "horizontal",
    paddingHorizontal: "s3",
    paddingVertical: "s0_75",
    borderRadius: "l",
  },
  roundedGhost: {
    borderColor: "ghostGrey",
    borderRadius: "max",
    borderWidth: 1,
    color: "textPrimary",
    fontSize: 14,
    fontWeight: "600",
    lineHeight: 14,
    paddingHorizontal: "s4",
    paddingBottom: "s2_5",
    paddingTop: "s2_25",
  },
  roundedWhiteEmpty: {
    backgroundColor: "white",
    borderRadius: "max",
    paddingHorizontal: "s4",
    paddingVertical: "s2_5",
  },
  roundedGreyEmpty: {
    backgroundColor: "greyButton",
    borderRadius: "max",
    paddingHorizontal: "s4",
    paddingVertical: "s2_5",
  },
  bonusGiveAwayButton: {
    backgroundColor: [
      "bonusGiveAwayGradientLeft",
      "bonusGiveAwayGradientRight",
    ],
    borderRadius: "max",
    paddingHorizontal: "s4",
    paddingVertical: "s2_5",
  },
  bonusDirectButton: {
    backgroundColor: ["bonusDirectGradientLeft", "bonusDirectGradientRight"],
    borderRadius: "max",
    paddingHorizontal: "s4",
    paddingVertical: "s2_5",
  },
  unlock: {
    backgroundColor: "white",
    color: "unlockButtonText",
    paddingHorizontal: "s4",
    paddingVertical: "s2",
    borderRadius: "max",
    fontSize: 15,
  },
};

export type Theme = typeof theme;

export const OPACITY_AD_CONTROLLING = 0.5;
